/**
 * Customer Ledger API service
 */
import apiClient from './api';

export interface TransactionType {
  INVOICE: 'invoice';
  PAYMENT: 'payment';
  CREDIT_NOTE: 'credit_note';
  DEBIT_NOTE: 'debit_note';
  OPENING_BALANCE: 'opening_balance';
  ADJUSTMENT: 'adjustment';
}

export interface CustomerLedgerEntry {
  id: string;
  customer_id: string;
  transaction_date: string;
  transaction_type: keyof TransactionType;
  reference_type: string;
  reference_number: string;
  reference_id: string;
  debit_amount: number;
  credit_amount: number;
  balance: number;
  description?: string;
  created_at: string;
  created_by_admin_id?: string;
}

export interface CustomerLedgerBalanceSummary {
  opening_balance: number;
  total_debits: number;
  total_credits: number;
  closing_balance: number;
}

export interface CustomerLedgerResponse {
  entries: CustomerLedgerEntry[];
  total_count: number;
  page: number;
  page_size: number;
  total_pages: number;
  balance_summary: CustomerLedgerBalanceSummary;
}

export interface CustomerLedgerSummary {
  customer_id: string;
  current_balance: number;
  transaction_counts: Record<string, number>;
  latest_transaction_date?: string;
  total_transactions: number;
}

export interface CustomerLedgerFilters {
  start_date?: string;
  end_date?: string;
  transaction_type?: keyof TransactionType;
  page?: number;
  page_size?: number;
}

export interface LedgerExportRequest {
  customer_id: string;
  start_date?: string;
  end_date?: string;
  transaction_type?: keyof TransactionType;
  format: 'pdf' | 'csv';
  include_summary?: boolean;
}

export interface LedgerExportResponse {
  export_id: string;
  download_url: string;
  expires_at: string;
  format: string;
  total_records: number;
}

export class CustomerLedgerService {
  /**
   * Get customer ledger entries with filtering and pagination
   */
  static async getCustomerLedger(
    customerId: string,
    filters?: CustomerLedgerFilters
  ): Promise<CustomerLedgerResponse> {
    const response = await apiClient.get(`/admin/customer-ledger/${customerId}`, {
      params: filters
    });
    return response.data;
  }

  /**
   * Get customer ledger summary information
   */
  static async getCustomerLedgerSummary(customerId: string): Promise<CustomerLedgerSummary> {
    const response = await apiClient.get(`/admin/customer-ledger/${customerId}/summary`);
    return response.data;
  }

  /**
   * Get current balance for a customer
   */
  static async getCustomerBalance(customerId: string): Promise<{
    customer_id: string;
    current_balance: number;
    balance_type: 'debit' | 'credit' | 'zero';
  }> {
    const response = await apiClient.get(`/admin/customer-ledger/${customerId}/balance`);
    return response.data;
  }

  /**
   * Download customer ledger as PDF
   */
  static async downloadLedgerPdf(
    customerId: string,
    filters?: Omit<CustomerLedgerFilters, 'page' | 'page_size'> & {
      include_summary?: boolean;
    }
  ): Promise<Blob> {
    const response = await apiClient.get(`/admin/customer-ledger/${customerId}/pdf`, {
      params: filters,
      responseType: 'blob'
    });
    return response.data;
  }

  /**
   * Export customer ledger to PDF or CSV format
   */
  static async exportCustomerLedger(
    customerId: string,
    exportRequest: LedgerExportRequest
  ): Promise<LedgerExportResponse> {
    const response = await apiClient.post(`/admin/customer-ledger/${customerId}/export`, exportRequest);
    return response.data;
  }

  /**
   * Get available transaction types for filtering
   */
  static async getTransactionTypes(): Promise<{
    transaction_types: Array<{ value: string; label: string }>;
  }> {
    // Return static transaction types instead of API call
    return {
      transaction_types: [
        { value: 'invoice', label: 'Invoice' },
        { value: 'payment', label: 'Payment' },
        { value: 'credit_note', label: 'Credit Note' },
        { value: 'debit_note', label: 'Debit Note' },
        { value: 'opening_balance', label: 'Opening Balance' },
        { value: 'adjustment', label: 'Adjustment' }
      ]
    };
  }
}